/**
 * Remediation C2 — Firestore-safe purchase order queries.
 * Rules require party-field or marketplace scoping; poNumber-only list queries are rejected.
 */
import {
  collection,
  type DocumentData,
  type Firestore,
  getDocs,
  limit,
  onSnapshot,
  query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  where,
} from 'firebase/firestore';
import Logger from './logger';
import {
  FIRESTORE_BROKER_PURCHASE_ORDERS_COLLECTION,
  FIRESTORE_PO_ARCHIVE_COLLECTION,
  FIRESTORE_PO_MARKETPLACE_FLAG,
  FIRESTORE_PURCHASE_ORDERS_COLLECTION,
} from '../constants/firestoreCollectionSecurity';
import { APPROVED_CARRIER_PENDING_RATE_CONFIRMATION } from '../constants/rateConfirmationConstants';

const SHIPPER_PO_PARTY_FIELDS = [
  'userId',
  'shipperId',
  'brokerId',
  'ownerId',
  'createdBy',
  'shipper',
  'carrierId',
] as const;

/** Shipper-owned list queries — userId legacy + shipperId party field (Phase 2). */
export const SHIPPER_PO_OWNER_PARTY_FIELDS = ['userId', 'shipperId'] as const;

const BROKER_PO_PARTY_FIELDS = [
  'brokerId',
  'userId',
  'ownerId',
  'createdBy',
  'carrierId',
] as const;

async function mergePartyFieldQueries(
  db: Firestore,
  collectionName: string,
  partyUid: string,
  partyFields: readonly string[],
  extraConstraints: QueryConstraint[] = []
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  const byId = new Map<string, QueryDocumentSnapshot<DocumentData>>();
  const queries: QueryConstraint[][] = [
    ...partyFields.map((field) => [where(field, '==', partyUid), ...extraConstraints]),
    [where('pendingCarrier.id', '==', partyUid), ...extraConstraints],
    [where('selectedCarrier.id', '==', partyUid), ...extraConstraints],
    [where('approvedCarrier.id', '==', partyUid), ...extraConstraints],
    [
      where('approvedCarrierPendingRateConfirmation.id', '==', partyUid),
      ...extraConstraints,
    ],
  ];

  await Promise.all(
    queries.map(async (constraints) => {
      try {
        const snap = await getDocs(query(collection(db, collectionName), ...constraints));
        snap.docs.forEach((d) => byId.set(d.id, d));
      } catch {
        // Composite index may be missing for nested fields — other party-field queries still apply.
      }
    })
  );

  return [...byId.values()];
}

async function getMarketplacePoDocsForPoNumber(
  db: Firestore,
  collectionName: string,
  poNumber: string
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  try {
    const snap = await getDocs(
      query(
        collection(db, collectionName),
        where('poNumber', '==', poNumber),
        where(FIRESTORE_PO_MARKETPLACE_FLAG, '==', true)
      )
    );
    return snap.docs.filter((d) => {
      const data = d.data();
      return !('carrierId' in data) || data.carrierId == null;
    });
  } catch {
    return [];
  }
}

export async function getPurchaseOrderDocsForPoAndParty(
  db: Firestore,
  poNumber: string,
  partyUid: string,
  extraConstraints: QueryConstraint[] = []
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  const partyDocs = await mergePartyFieldQueries(
    db,
    FIRESTORE_PURCHASE_ORDERS_COLLECTION,
    partyUid,
    SHIPPER_PO_PARTY_FIELDS,
    [where('poNumber', '==', poNumber), ...extraConstraints]
  );
  const marketplaceDocs = await getMarketplacePoDocsForPoNumber(
    db,
    FIRESTORE_PURCHASE_ORDERS_COLLECTION,
    poNumber
  );
  const byId = new Map<string, QueryDocumentSnapshot<DocumentData>>();
  [...partyDocs, ...marketplaceDocs].forEach((d) => byId.set(d.id, d));
  return [...byId.values()];
}

export async function getBrokerPurchaseOrderDocsForPoAndParty(
  db: Firestore,
  poNumber: string,
  partyUid: string,
  extraConstraints: QueryConstraint[] = []
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  const partyDocs = await mergePartyFieldQueries(
    db,
    FIRESTORE_BROKER_PURCHASE_ORDERS_COLLECTION,
    partyUid,
    BROKER_PO_PARTY_FIELDS,
    [where('poNumber', '==', poNumber), ...extraConstraints]
  );
  const marketplaceDocs = await getMarketplacePoDocsForPoNumber(
    db,
    FIRESTORE_BROKER_PURCHASE_ORDERS_COLLECTION,
    poNumber
  );
  const byId = new Map<string, QueryDocumentSnapshot<DocumentData>>();
  [...partyDocs, ...marketplaceDocs].forEach((d) => byId.set(d.id, d));
  return [...byId.values()];
}

/** Child POs for a parent — scoped to shipper owner. */
export async function getShipperChildPurchaseOrdersForParent(
  db: Firestore,
  parentPONumber: string,
  shipperUid: string
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  return mergePartyFieldQueries(
    db,
    FIRESTORE_PURCHASE_ORDERS_COLLECTION,
    shipperUid,
    SHIPPER_PO_OWNER_PARTY_FIELDS,
    [where('parentPONumber', '==', parentPONumber)]
  );
}

/** One-time fetch of shipper-owned PO docs (merged userId + shipperId pipelines). */
export async function getShipperOwnedPurchaseOrderDocs(
  db: Firestore,
  shipperUid: string,
  extraConstraints: QueryConstraint[] = []
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  return mergePartyFieldQueries(
    db,
    FIRESTORE_PURCHASE_ORDERS_COLLECTION,
    shipperUid,
    SHIPPER_PO_OWNER_PARTY_FIELDS,
    extraConstraints
  );
}

/**
 * Owner-field merge only (no nested carrier party queries).
 * Used for poArchive — archived copies are shipper/broker owned, not carrier-assigned listings.
 */
async function mergeOwnerPartyFieldQueries(
  db: Firestore,
  collectionName: string,
  partyUid: string,
  partyFields: readonly string[],
  extraConstraints: QueryConstraint[] = []
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  const byId = new Map<string, QueryDocumentSnapshot<DocumentData>>();
  await Promise.all(
    partyFields.map(async (field) => {
      try {
        const snap = await getDocs(
          query(collection(db, collectionName), where(field, '==', partyUid), ...extraConstraints)
        );
        snap.docs.forEach((d) => byId.set(d.id, d));
      } catch (err) {
        Logger.error(
          `[PurchaseOrderQueries] ${collectionName} ${field} owner query error:`,
          err instanceof Error ? err : new Error(String(err))
        );
      }
    })
  );
  return [...byId.values()];
}

/** One-time fetch of shipper-owned poArchive docs (merged userId + shipperId). */
export async function getShipperOwnedPoArchiveDocs(
  db: Firestore,
  shipperUid: string,
  extraConstraints: QueryConstraint[] = []
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  return mergeOwnerPartyFieldQueries(
    db,
    FIRESTORE_PO_ARCHIVE_COLLECTION,
    shipperUid,
    SHIPPER_PO_OWNER_PARTY_FIELDS,
    extraConstraints
  );
}

/** Parent multi-load PO for schedule filtering — party-scoped (not userId-only). */
export async function getShipperParentMultiLoadPurchaseOrder(
  db: Firestore,
  parentPONumber: string,
  shipperUid: string
): Promise<QueryDocumentSnapshot<DocumentData> | null> {
  const docs = await mergePartyFieldQueries(
    db,
    FIRESTORE_PURCHASE_ORDERS_COLLECTION,
    shipperUid,
    SHIPPER_PO_OWNER_PARTY_FIELDS,
    [
      where('poNumber', '==', parentPONumber),
      where('isMultiLoad', '==', true),
      limit(1),
    ]
  );
  return docs[0] ?? null;
}

/** Parent multi-load broker PO — party-scoped (brokerId). */
export async function getBrokerParentMultiLoadPurchaseOrder(
  db: Firestore,
  parentPONumber: string,
  brokerUid: string
): Promise<QueryDocumentSnapshot<DocumentData> | null> {
  const docs = await mergePartyFieldQueries(
    db,
    FIRESTORE_BROKER_PURCHASE_ORDERS_COLLECTION,
    brokerUid,
    ['brokerId'],
    [
      where('poNumber', '==', parentPONumber),
      where('isMultiLoad', '==', true),
      limit(1),
    ]
  );
  return docs[0] ?? null;
}

/**
 * Owned multi-load child PO for shipper assignment submit — R2 party-scoped.
 * Prefer owner fields so Submit never uses poNumber-only list queries.
 */
export async function getShipperOwnedMultiLoadChildPurchaseOrder(
  db: Firestore,
  childPONumber: string,
  parentPONumber: string,
  shipperUid: string
): Promise<QueryDocumentSnapshot<DocumentData> | null> {
  const docs = await getShipperOwnedPurchaseOrderDocs(db, shipperUid, [
    where('poNumber', '==', childPONumber),
    where('isMultiLoad', '==', true),
    where('parentPONumber', '==', parentPONumber),
  ]);
  return docs[0] ?? null;
}

/** Owned multi-load child PO for broker assignment submit — R2 party-scoped. */
export async function getBrokerOwnedMultiLoadChildPurchaseOrder(
  db: Firestore,
  childPONumber: string,
  parentPONumber: string,
  brokerUid: string
): Promise<QueryDocumentSnapshot<DocumentData> | null> {
  const docs = await mergePartyFieldQueries(
    db,
    FIRESTORE_BROKER_PURCHASE_ORDERS_COLLECTION,
    brokerUid,
    ['brokerId'],
    [
      where('poNumber', '==', childPONumber),
      where('isMultiLoad', '==', true),
      where('parentPONumber', '==', parentPONumber),
    ]
  );
  return docs[0] ?? null;
}

function mergeShipperOwnedSnapshotDocs(
  userIdDocs: QueryDocumentSnapshot<DocumentData>[],
  shipperIdDocs: QueryDocumentSnapshot<DocumentData>[]
): QueryDocumentSnapshot<DocumentData>[] {
  const byId = new Map<string, QueryDocumentSnapshot<DocumentData>>();
  [...userIdDocs, ...shipperIdDocs].forEach((docSnap) => byId.set(docSnap.id, docSnap));
  return [...byId.values()];
}

/**
 * Real-time shipper-owned purchaseOrders — dual listeners on userId + shipperId (Phase 2).
 * Mirrors broker notification split pipelines; dedupes by document id.
 */
export function subscribeToShipperOwnedPurchaseOrders(
  db: Firestore,
  shipperUid: string,
  extraConstraints: QueryConstraint[],
  onUpdate: (docs: QueryDocumentSnapshot<DocumentData>[]) => void,
  onError?: (error: Error) => void
): () => void {
  const handleListenerError = (channel: string) => (err: Error) => {
    Logger.error(`[PurchaseOrderQueries] ${channel} shipper PO listener error:`, err);
    onError?.(err);
  };

  let userIdDocs: QueryDocumentSnapshot<DocumentData>[] = [];
  let shipperIdDocs: QueryDocumentSnapshot<DocumentData>[] = [];

  const emit = () => {
    onUpdate(mergeShipperOwnedSnapshotDocs(userIdDocs, shipperIdDocs));
  };

  const userIdQuery = query(
    collection(db, FIRESTORE_PURCHASE_ORDERS_COLLECTION),
    where('userId', '==', shipperUid),
    ...extraConstraints
  );
  const shipperIdQuery = query(
    collection(db, FIRESTORE_PURCHASE_ORDERS_COLLECTION),
    where('shipperId', '==', shipperUid),
    ...extraConstraints
  );

  const unsubscribeUserId = onSnapshot(
    userIdQuery,
    (snapshot) => {
      userIdDocs = snapshot.docs;
      emit();
    },
    (err) => {
      handleListenerError('userId')(err);
      userIdDocs = [];
      emit();
    }
  );

  const unsubscribeShipperId = onSnapshot(
    shipperIdQuery,
    (snapshot) => {
      shipperIdDocs = snapshot.docs;
      emit();
    },
    (err) => {
      handleListenerError('shipperId')(err);
      shipperIdDocs = [];
      emit();
    }
  );

  return () => {
    unsubscribeUserId();
    unsubscribeShipperId();
  };
}

/** Child POs for a parent — scoped to broker owner. */
export async function getBrokerChildPurchaseOrdersForParent(
  db: Firestore,
  parentPONumber: string,
  brokerUid: string
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  return mergePartyFieldQueries(
    db,
    FIRESTORE_BROKER_PURCHASE_ORDERS_COLLECTION,
    brokerUid,
    ['brokerId'],
    [where('parentPONumber', '==', parentPONumber)]
  );
}

export async function findPurchaseOrderForParty(
  db: Firestore,
  poNumber: string,
  partyUid: string | undefined,
  preferBroker: boolean
): Promise<{ id: string; sourceCollection: string; [key: string]: unknown } | null> {
  const collections = preferBroker
    ? ([
        {
          name: FIRESTORE_BROKER_PURCHASE_ORDERS_COLLECTION,
          fetch: getBrokerPurchaseOrderDocsForPoAndParty,
        },
        {
          name: FIRESTORE_PURCHASE_ORDERS_COLLECTION,
          fetch: getPurchaseOrderDocsForPoAndParty,
        },
      ] as const)
    : ([
        {
          name: FIRESTORE_PURCHASE_ORDERS_COLLECTION,
          fetch: getPurchaseOrderDocsForPoAndParty,
        },
        {
          name: FIRESTORE_BROKER_PURCHASE_ORDERS_COLLECTION,
          fetch: getBrokerPurchaseOrderDocsForPoAndParty,
        },
      ] as const);

  for (const { name, fetch } of collections) {
    const docs = partyUid ? await fetch(db, poNumber, partyUid) : [];
    if (docs.length > 0) {
      const docSnap = docs[0];
      return { id: docSnap.id, sourceCollection: name, ...docSnap.data() };
    }
    const marketplaceOnly = await getMarketplacePoDocsForPoNumber(db, name, poNumber);
    if (marketplaceOnly.length > 0) {
      const docSnap = marketplaceOnly[0];
      return { id: docSnap.id, sourceCollection: name, ...docSnap.data() };
    }
  }

  return null;
}

/** Carrier PO lookup when shipper/broker accepted counter — pending rate confirmation snapshot matches carrier. */
export async function findPurchaseOrderForPendingRateConfirmationCarrier(
  db: Firestore,
  poNumber: string,
  carrierId: string
): Promise<{ id: string; sourceCollection: string; [key: string]: unknown } | null> {
  if (!poNumber || !carrierId) return null;

  // R2: poNumber-only list queries are denied — use party-scoped helpers (includes pending RC nested id).
  const brokerDocs = await getBrokerPurchaseOrderDocsForPoAndParty(db, poNumber, carrierId);
  for (const docSnap of brokerDocs) {
    const data = docSnap.data();
    const pending = data[APPROVED_CARRIER_PENDING_RATE_CONFIRMATION] as
      | { id?: string }
      | null
      | undefined;
    if (pending && typeof pending === 'object' && pending.id === carrierId) {
      return {
        id: docSnap.id,
        sourceCollection: FIRESTORE_BROKER_PURCHASE_ORDERS_COLLECTION,
        ...data,
      };
    }
  }

  const shipperDocs = await getPurchaseOrderDocsForPoAndParty(db, poNumber, carrierId);
  for (const docSnap of shipperDocs) {
    const data = docSnap.data();
    const pending = data[APPROVED_CARRIER_PENDING_RATE_CONFIRMATION] as
      | { id?: string }
      | null
      | undefined;
    if (pending && typeof pending === 'object' && pending.id === carrierId) {
      return {
        id: docSnap.id,
        sourceCollection: FIRESTORE_PURCHASE_ORDERS_COLLECTION,
        ...data,
      };
    }
  }

  return null;
}

/** True when PO has vendor/ship-to fields required by ShippingScheduleForm prefill. */
export function shipperPoHasShippingFormFields(
  po: Record<string, unknown> | null | undefined
): boolean {
  if (!po) return false;
  const vendor = po.vendorInfo as Record<string, unknown> | undefined;
  const shipTo = po.shipTo as Record<string, unknown> | undefined;
  return Boolean(vendor?.streetAddress && shipTo?.streetAddress);
}

/** Full shipper PO doc for carrier/shipping workflow navigation (party-scoped query). */
export async function getShipperPurchaseOrderRecordForWorkflow(
  db: Firestore,
  poNumber: string,
  partyUid: string
): Promise<Record<string, unknown> | null> {
  const docs = await getPurchaseOrderDocsForPoAndParty(db, poNumber, partyUid);
  if (docs.length === 0) return null;
  const docSnap = docs[0];
  return { id: docSnap.id, ...docSnap.data() };
}

const FIRESTORE_IN_QUERY_MAX = 30;

/** Batch fetch purchaseOrders by poNumber (dashboard notification validation). */
export async function batchGetPurchaseOrderDocsByPoNumber(
  db: Firestore,
  poNumbers: string[]
): Promise<Map<string, QueryDocumentSnapshot<DocumentData>[]>> {
  const unique = [...new Set(poNumbers.map((p) => p.trim()).filter(Boolean))];
  const result = new Map<string, QueryDocumentSnapshot<DocumentData>[]>();
  if (unique.length === 0) return result;

  for (let i = 0; i < unique.length; i += FIRESTORE_IN_QUERY_MAX) {
    const chunk = unique.slice(i, i + FIRESTORE_IN_QUERY_MAX);
    const snap = await getDocs(
      query(
        collection(db, FIRESTORE_PURCHASE_ORDERS_COLLECTION),
        where('poNumber', 'in', chunk)
      )
    );
    snap.docs.forEach((docSnap) => {
      const pn = String(docSnap.data().poNumber || '').trim();
      if (!pn) return;
      const list = result.get(pn) || [];
      list.push(docSnap);
      result.set(pn, list);
    });
  }

  return result;
}
